import React, { useState, useRef, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Chip,
  ClickAwayListener,
  Popper,
  Fade,
  Collapse,
  InputBase,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Bell,
  Search,
  Settings,
  LogOut,
  User,
  Moon,
  Sun,
  Users,
  FileText,
  ShoppingCart,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import routes from '../config/routes';
import { useSearch } from '../context/SearchContext';
import VirtualPATLogo, { HeaderLogo } from './virtualpat-theme/VirtualPATLogo';

function ModernHealthcareHeader({
  user,
  onMenuClick,
  onLogout,
  darkMode = false,
  onToggleDarkMode,
  appMode,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notificationAnchor, setNotificationAnchor] = React.useState(null);

  // Search functionality
  const {
    searchQuery,
    searchResults,
    handleSearch,
    handleResultClick,
    isSearchOpen,
    setIsSearchOpen,
  } = useSearch();
  const searchInputRef = useRef(null);
  const [searchAnchorEl, setSearchAnchorEl] = useState(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const handleProfileMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationOpen = event => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleMenuClose();
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    handleMenuClose();
  };

  const mockNotifications = [
    { id: 1, title: 'New patient assigned', time: '5 min ago', type: 'info' },
    { id: 2, title: 'Critical flag: John Doe', time: '10 min ago', type: 'warning' },
    { id: 3, title: 'Surgery schedule updated', time: '1 hour ago', type: 'success' },
  ];

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor:
          theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(30, 41, 59, 0.8)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: '0 1px 40px rgba(0, 0, 0, 0.02)',
      }}
    >
      <Toolbar sx={{ px: { xs: 1, sm: 2, md: 3 }, minHeight: 64, height: 64 }}>
        {/* Mobile: Menu Button + V Icon Logo */}
        {isMobile && (
          <>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={onMenuClick}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                overflow: 'hidden',
              }}
            >
              <VirtualPATLogo
                variant="icon"
                size="small"
                onClick={() => navigate('/')}
                sx={{
                  cursor: 'pointer',
                  height: 32,
                  width: 32,
                }}
              />
            </Box>
          </>
        )}

        {/* Tablet: Show V icon logo on the left */}
        {!isMobile && isTablet && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mr: 2,
            }}
          >
            <VirtualPATLogo
              variant="icon"
              size="medium"
              onClick={() => navigate('/')}
              sx={{
                cursor: 'pointer',
                height: 40,
                width: 40,
              }}
            />
          </Box>
        )}

        {/* Desktop Logo - Show full logo on desktop */}
        {!isMobile && !isTablet && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mr: 4,
              minWidth: 200, // Reserve space for logo
            }}
          >
            <VirtualPATLogo
              variant="full"
              size="medium"
              onClick={() => navigate('/')}
              sx={{
                cursor: 'pointer',
                height: 40,
              }}
            />
          </Box>
        )}

        {/* Search Bar - Desktop Only (centered) */}
        {!isMobile && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: 500,
              }}
            >
              <Search
                size={20}
                style={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: theme.palette.text.secondary,
                }}
              />
              <input
                ref={searchInputRef}
                placeholder="Search patients, orders, protocols..."
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                onFocus={e => {
                  setSearchAnchorEl(e.currentTarget);
                  setIsSearchOpen(true);
                  e.target.style.borderColor = theme.palette.primary.main;
                  e.target.style.boxShadow = `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`;
                }}
                onBlur={e => {
                  e.target.style.borderColor = alpha(theme.palette.primary.main, 0.2);
                  e.target.style.boxShadow = 'none';
                  // Delay closing to allow result clicks
                  setTimeout(() => {
                    setIsSearchOpen(false);
                    setSearchAnchorEl(null);
                  }, 200);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 48px',
                  borderRadius: '12px',
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  background: theme.palette.glass?.light || 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  fontSize: '0.875rem',
                  color: theme.palette.text.primary,
                  outline: 'none',
                  transition: 'all 0.2s',
                }}
              />
            </Box>
          </Box>
        )}

        {/* ASC Mode Badge */}
        {appMode === 'asc' && !isMobile && (
          <Chip
            label="ASC Mode"
            color="primary"
            size="medium"
            sx={{
              mx: 2,
              fontWeight: 600,
              backgroundColor: theme.palette.primary.main,
              color: 'white',
            }}
          />
        )}

        {/* Right Side Actions */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 },
            minWidth: { lg: 200 }, // Match logo area width on desktop
            justifyContent: 'flex-end',
          }}
        >
          {/* Mobile Search Icon */}
          {isMobile && (
            <IconButton
              onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              sx={{
                p: { xs: 0.75, sm: 1 },
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <Search size={18} />
            </IconButton>
          )}

          {/* Dark Mode Toggle */}
          <IconButton
            onClick={onToggleDarkMode}
            sx={{
              p: { xs: 0.75, sm: 1 },
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </IconButton>

          {/* Notifications */}
          <IconButton
            onClick={handleNotificationOpen}
            sx={{
              p: { xs: 0.75, sm: 1 },
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            <Badge
              badgeContent={3}
              color="error"
              sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}
            >
              <Bell size={18} />
            </Badge>
          </IconButton>

          {/* User Menu */}
          {user ? (
            <Button
              onClick={handleProfileMenuOpen}
              sx={{
                ml: { xs: 0.5, sm: 1 },
                borderRadius: '12px',
                padding: { xs: '6px', sm: '8px 16px' },
                minWidth: { xs: 'auto', sm: 64 },
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
              startIcon={
                <Avatar
                  sx={{
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    background: theme.palette.primary.gradient || theme.palette.primary.main,
                    fontSize: { xs: '12px', sm: '14px' },
                    fontWeight: 700,
                  }}
                >
                  {user?.name?.charAt(0).toUpperCase()}
                </Avatar>
              }
            >
              {!isMobile && (
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" fontWeight={700}>
                    {user?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.role}
                  </Typography>
                </Box>
              )}
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate(routes.login())}
              sx={{
                ml: 1,
                borderRadius: '12px',
                padding: '8px 16px',
              }}
            >
              Login
            </Button>
          )}
        </Box>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationClose}
          slotProps={{
            paper: {
              sx: {
                mt: 1.5,
                minWidth: 320,
                maxHeight: 400,
                background:
                  theme.palette.mode === 'light'
                    ? 'rgba(255, 255, 255, 0.95)'
                    : 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: '16px',
                overflow: 'visible',
                '&::before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 20,
                  width: 10,
                  height: 10,
                  bgcolor: 'inherit',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  borderRight: 'none',
                  borderBottom: 'none',
                },
              },
            },
          }}
        >
          <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="h6" fontWeight={700}>
              Notifications
            </Typography>
          </Box>
          {mockNotifications.map(notification => (
            <MenuItem
              key={notification.id}
              onClick={handleNotificationClose}
              sx={{
                py: 2,
                px: 2,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {notification.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {notification.time}
                </Typography>
              </Box>
            </MenuItem>
          ))}
          <Box sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Button fullWidth variant="outlined" size="small" sx={{ borderRadius: '8px' }}>
              View All Notifications
            </Button>
          </Box>
        </Menu>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          slotProps={{
            paper: {
              sx: {
                mt: 1.5,
                minWidth: 200,
                background:
                  theme.palette.mode === 'light'
                    ? 'rgba(255, 255, 255, 0.95)'
                    : 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: '12px',
              },
            },
          }}
        >
          <MenuItem onClick={handleProfileClick}>
            <User size={18} style={{ marginRight: 12 }} />
            Profile
          </MenuItem>
          <MenuItem onClick={handleSettingsClick}>
            <Settings size={18} style={{ marginRight: 12 }} />
            Settings
          </MenuItem>
          <Divider sx={{ my: 1 }} />
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onLogout();
            }}
          >
            <LogOut size={18} style={{ marginRight: 12 }} />
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>

      {/* Mobile Search Bar */}
      {isMobile && (
        <Collapse in={mobileSearchOpen} timeout="auto" unmountOnExit>
          <Box
            sx={{
              p: 2,
              backgroundColor: theme.palette.background.paper,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
              }}
            >
              <Search
                size={20}
                style={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: theme.palette.text.secondary,
                }}
              />
              <InputBase
                placeholder="Search patients, orders, protocols..."
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                onFocus={e => {
                  setSearchAnchorEl(e.currentTarget);
                  setIsSearchOpen(true);
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setIsSearchOpen(false);
                    setSearchAnchorEl(null);
                  }, 200);
                }}
                sx={{
                  width: '100%',
                  pl: 6,
                  pr: 2,
                  py: 1.5,
                  borderRadius: '12px',
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backgroundColor: alpha(theme.palette.background.default, 0.8),
                  '&:focus': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                  },
                  transition: 'all 0.2s',
                }}
                inputProps={{
                  'aria-label': 'search',
                }}
              />
            </Box>
          </Box>
        </Collapse>
      )}

      {/* Search Results Dropdown */}
      <Popper
        open={
          isSearchOpen &&
          searchAnchorEl !== null &&
          (searchQuery.length >= 2 || searchResults.loading)
        }
        anchorEl={searchAnchorEl}
        placement={isMobile ? 'bottom' : 'bottom-start'}
        transition
        style={{ zIndex: 1400, width: isMobile ? 'calc(100% - 32px)' : 'auto' }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
          ...(isMobile
            ? [
                {
                  name: 'preventOverflow',
                  options: {
                    padding: 16,
                  },
                },
              ]
            : []),
        ]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper
              sx={{
                mt: 1,
                minWidth: isMobile ? '100%' : 400,
                maxHeight: 400,
                overflow: 'auto',
                background:
                  theme.palette.mode === 'light'
                    ? 'rgba(255, 255, 255, 0.98)'
                    : 'rgba(30, 41, 59, 0.98)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: '16px',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              }}
            >
              {searchResults.loading ? (
                <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : searchResults.error ? (
                <Box sx={{ p: 3 }}>
                  <Typography color="error" variant="body2">
                    {searchResults.error}
                  </Typography>
                </Box>
              ) : searchResults.patients.length === 0 &&
                searchResults.protocols.length === 0 &&
                searchResults.orders.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <Typography color="text.secondary" variant="body2">
                    No results found for "{searchQuery}"
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {/* Patients Section */}
                  {searchResults.patients.length > 0 && (
                    <>
                      <Box sx={{ px: 2, py: 1, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                        <Typography variant="caption" fontWeight={600} color="primary">
                          PATIENTS
                        </Typography>
                      </Box>
                      {searchResults.patients.map(patient => (
                        <ListItem
                          key={patient.id}
                          button
                          onClick={() => handleResultClick('patient', patient)}
                          sx={{
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Users size={20} />
                          </ListItemIcon>
                          <ListItemText
                            primary={patient.name}
                            secondary={`MRN: ${patient.mrn || patient.id}`}
                          />
                        </ListItem>
                      ))}
                    </>
                  )}

                  {/* Protocols Section */}
                  {searchResults.protocols.length > 0 && (
                    <>
                      <Box sx={{ px: 2, py: 1, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                        <Typography variant="caption" fontWeight={600} color="primary">
                          PROTOCOLS
                        </Typography>
                      </Box>
                      {searchResults.protocols.map(protocol => (
                        <ListItem
                          key={protocol.id}
                          button
                          onClick={() => handleResultClick('protocol', protocol)}
                          sx={{
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                            },
                          }}
                        >
                          <ListItemIcon>
                            <FileText size={20} />
                          </ListItemIcon>
                          <ListItemText primary={protocol.name} secondary={protocol.surgeryType} />
                        </ListItem>
                      ))}
                    </>
                  )}

                  {/* Orders Section */}
                  {searchResults.orders.length > 0 && (
                    <>
                      <Box sx={{ px: 2, py: 1, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                        <Typography variant="caption" fontWeight={600} color="primary">
                          ORDERS
                        </Typography>
                      </Box>
                      {searchResults.orders.map(order => (
                        <ListItem
                          key={order.id}
                          button
                          onClick={() => handleResultClick('order', order)}
                          sx={{
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                            },
                          }}
                        >
                          <ListItemIcon>
                            <ShoppingCart size={20} />
                          </ListItemIcon>
                          <ListItemText primary={order.name} secondary={order.category} />
                        </ListItem>
                      ))}
                    </>
                  )}
                </List>
              )}
            </Paper>
          </Fade>
        )}
      </Popper>
    </AppBar>
  );
}

export default ModernHealthcareHeader;
